import { useMemo } from 'react';
import { useNumericFormat } from 'react-number-format';
import cx from 'classnames';
import { Plan } from '@/api';
import { SaveBadge } from '@/components/SaveBadge';
import { TariffType } from '@/config';
import { calculateSavePercentage, getPrice } from '@/helpers';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/redux/hooks';
import { paymentSelectors } from '@/redux/selectors';

import styles from './TariffEntry.module.sass';

interface TariffEntryProps {
  isActive?: boolean;
  planCategory: TariffType;
  item: Plan;
  priceCustomText?: string;
  onClick: () => void;
}

const TariffEntry = (props: TariffEntryProps) => {
  const { isActive, planCategory, item, priceCustomText, onClick } = props;
  const { t } = useTranslation();
  const { biggestPricePerCheck, paymentPlans } = useAppSelector(
    paymentSelectors.getPaymentSlice,
  );

  const numberFormat = useNumericFormat({
    thousandSeparator: ',',
    decimalScale: 2,
  });

  const { price, pricePerCheck, oldPrice, discount } = getPrice(
    item.price,
    item.value,
    item.old_price,
    0,
  );

  const classes = cx(styles.entry, {
    [styles[planCategory]]: !!planCategory,
    [styles['is-active']]: isActive,
    [styles['is-clickable']]: !!onClick,
  });

  const savePercentage = useMemo(() => {
    return calculateSavePercentage(
      pricePerCheck,
      biggestPricePerCheck,
      Number(paymentPlans?.baseUsdPrice),
    );
  }, [pricePerCheck, biggestPricePerCheck]);

  return (
    <button type="button" className={classes} onClick={onClick}>
      <div className={styles.body}>
        <div className={styles.top}>
          <div className={styles.title}>{item.name}</div>
          {!!savePercentage && (
            <SaveBadge
              isActive={isActive}
              tariff={planCategory}
              value={savePercentage}
              className={styles.save}
            />
          )}
        </div>

        {!!pricePerCheck && (
          <div className={styles.description}>
            ${t('translation.invoices_price_str', { count: pricePerCheck })}
            <span className={styles.dot}>&middot;</span>
            {t('translation.invoices_price_valid_period_str')}
          </div>
        )}
      </div>

      <div className={styles.prices}>
        {!!priceCustomText && (
          <div className={styles.priceCustomText}>{priceCustomText}</div>
        )}

        {!!price && (
          <div className={styles.price}>
            ${numberFormat.format?.(price.toString())}
          </div>
        )}
        {!!discount && (
          <div className={styles.oldPrice}>
            ${numberFormat.format?.(oldPrice.toString())}
          </div>
        )}
      </div>
    </button>
  );
};

export default TariffEntry;
